import React from 'react'
import qs from 'qs'
import DealerRankingStoresContainer from './dealer_ranking_stores_container'
import {
  PURCHASE_METHODS,
  spRankLabel,
  storePath,
  bestKaitoriHistory,
  priceDateLabel,
  displayPricePc,
  conditionLabelPc,
  stars,
  formatRating,
  storeField,
  shouldShowPhone,
  hasRecentAssessment,
  freeShippingTag,
  isCertifiedStore,
} from './dealer_ranking_redesign_utils'

function DealerRankingPcCard({ entry, rank, item, isHighest }) {
  const store = entry.store || {}
  const contract = store.store_contract || store.storeContract || {}
  const path = storePath(store)
  const rating = store.rating || 0
  const reviewCount = store.review_count || 0
  const satisfaction = Math.round((store.satisfaction_rate || 0) * 100)
  const pm = store.purchase_method || '000'
  const history = bestKaitoriHistory(entry)
  const dateLabel = priceDateLabel(entry, history)
  const priceLabel = displayPricePc(entry, history)
  const condition = conditionLabelPc(entry, history)
  const phoneNumber = storeField(store, 'phone_number', 'phoneNumber')
  const showPhone = shouldShowPhone(store)
  const showEstimateForm =
    (contract.assessment_form || contract.assessmentForm) &&
    !(store.suspended_estimate_form ?? store.suspendedEstimateForm)
  const showClickTransfer =
    (contract.click_transfer || contract.clickTransfer) &&
    !(store.suspended_click_transfer ?? store.suspendedClickTransfer)

  const cardClass = isHighest
    ? 'dealer-ranking-pc__card dealer-ranking-pc__card--highest'
    : 'dealer-ranking-pc__card'

  const tags = []
  if (hasRecentAssessment(entry)) tags.push({ label: '直近査定あり', variant: 'recent' })
  PURCHASE_METHODS.forEach((label, i) => {
    if (pm[i] === '1') tags.push({ label, variant: 'method' })
  })
  const shipping = freeShippingTag(store)
  if (shipping) tags.push({ label: shipping, variant: 'method' })

  let primaryBtn = null
  if (showEstimateForm) {
    const query = qs.stringify({ store_id: store.id, item_id: item.id })
    primaryBtn = (
      <a href={`/store_estimate_form/?${query}`} rel="nofollow" className="dealer-ranking-pc__btn--primary">
        無料で査定依頼する →
      </a>
    )
  } else if (showClickTransfer) {
    primaryBtn = (
      <a
        href={contract.click_transfer_path || contract.clickTransferPath || path}
        target="_blank"
        rel="noopener noreferrer nofollow"
        className="dealer-ranking-pc__btn--primary"
      >
        公式サイトへ →
      </a>
    )
  } else {
    primaryBtn = (
      <a href={path} className="dealer-ranking-pc__btn--primary">
        詳細へ →
      </a>
    )
  }

  const secondaryBtn = showPhone ? (
    <a
      href={`tel:${phoneNumber}`}
      className="dealer-ranking-pc__btn--phone"
      data-analytics-track-event="電話（お問い合わせ） click"
    >
      <span className="dealer-ranking-pc__btn--phone-icon i fa fa-phone-volume" aria-hidden="true" />
      {phoneNumber}
    </a>
  ) : (showEstimateForm || showClickTransfer) ? (
    <a href={path} className="dealer-ranking-pc__btn--secondary">
      店舗詳細を見る
    </a>
  ) : null

  return (
    <article className={cardClass}>
      {isHighest ? <span className="dealer-ranking-pc__badge-highest">HIGHEST</span> : null}
      <div className="dealer-ranking-pc__card-inner">
        <div className="dealer-ranking-pc__rank-col">
          <span className="dealer-ranking-pc__rank">{spRankLabel(rank)}</span>
        </div>
        <div className="dealer-ranking-pc__store-col">
          <div className="dealer-ranking-pc__store-body">
            <div className="dealer-ranking-pc__store-header">
              <a href={path} className="dealer-ranking-pc__store-name">
                {store.name}
              </a>
              {isCertifiedStore(store) ? (
                <span className="dealer-ranking-pc__certified-badge">
                  <span className="dealer-ranking-pc__certified-badge-icon" aria-hidden="true">
                    ✓
                  </span>
                  認証店舗
                </span>
              ) : null}
            </div>
            <div className="dealer-ranking-pc__rating">
              <span className="dealer-ranking-pc__stars" aria-hidden="true">
                {stars(rating)}
              </span>
              <span className="dealer-ranking-pc__rating-value">{formatRating(rating)}</span>
              <span className="dealer-ranking-pc__rating-meta">
                ({reviewCount.toLocaleString()}件のクチコミ)
              </span>
              <span className="dealer-ranking-pc__satisfaction">満足度 {satisfaction}%</span>
            </div>
            {tags.length > 0 ? (
              <div className="dealer-ranking-pc__tags">
                {tags.map((tag) => (
                  <span
                    key={tag.label}
                    className={`dealer-ranking-pc__tag dealer-ranking-pc__tag--${tag.variant}`}
                  >
                    {tag.label}
                  </span>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <div className="dealer-ranking-pc__price-col">
          {dateLabel ? <p className="dealer-ranking-pc__date">{dateLabel}</p> : null}
          <p className="dealer-ranking-pc__price">{priceLabel}</p>
          {condition ? <p className="dealer-ranking-pc__condition">{condition}</p> : null}
        </div>
        <div className="dealer-ranking-pc__actions-col">
          {primaryBtn}
          {secondaryBtn}
        </div>
      </div>
    </article>
  )
}

function DealerRankingRedesignPcView({
  item,
  prPrices,
  displayList,
  remaining,
  canCollapse,
  loading,
  currentSortKey,
  listLength,
  onSortChange,
  onLoadMore,
  onCollapse,
}) {
  let rank = 0
  const entries = [...(prPrices || []), ...displayList]

  const cards = entries.map((entry) => {
    rank += 1
    const isHighest = rank === 1
    const key = entry.store && entry.store.id ? `store-${entry.store.id}-${rank}` : `rank-${rank}`
    return (
      <DealerRankingPcCard
        key={key}
        entry={entry}
        rank={rank}
        item={item}
        isHighest={isHighest}
      />
    )
  })

  return (
    <>
      <div className="dealer-ranking-pc__sort-row">
        <label className="dealer-ranking-pc__sort-label" htmlFor="dealer-ranking-pc-sort">
          並び替え
        </label>
        <select
          id="dealer-ranking-pc-sort"
          className="dealer-ranking-pc__sort"
          value={currentSortKey}
          onChange={onSortChange}
        >
          <option value="">おすすめ順</option>
          <option value="price">値段が高い順</option>
          <option value="review">評価順</option>
        </select>
      </div>
      <div className="dealer-ranking-pc__list">
        {loading && listLength === 0 ? <p className="dealer-ranking-pc__loading">読み込み中...</p> : null}
        {cards}
      </div>
      {remaining > 0 || canCollapse ? (
        <button
          type="button"
          className="dealer-ranking-pc__view-all"
          onClick={remaining > 0 ? onLoadMore : onCollapse}
          disabled={loading}
        >
          {loading ? '読み込み中...' : remaining > 0 ? `残り${remaining}社を見る` : '閉じる'}
        </button>
      ) : null}
    </>
  )
}

function DealerRankingRedesignPc(props) {
  return (
    <DealerRankingStoresContainer {...props}>
      {(viewState) => <DealerRankingRedesignPcView {...viewState} />}
    </DealerRankingStoresContainer>
  )
}

export default DealerRankingRedesignPc
